import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Table, Select, Input, Breadcrumb, Tag } from 'antd';
import { showConfirmModal } from '../components/PopupModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { tenantService, Tenant } from '../services/tenants';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { Option } = Select;

interface TenantTableData extends Tenant {
  key: string;
  no: number;
}

const TenantIntro: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [tenants, setTenants] = useState<TenantTableData[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined as 'active' | 'inactive' | 'closed' | undefined,
    approvalStatus: undefined as 'pending' | 'approved' | 'rejected' | undefined,
    search: '',
  });

  // API 호출 함수
  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await tenantService.getTenants({
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status,
        approvalStatus: filters.approvalStatus,
        search: filters.search || undefined,
      });

      const tableData: TenantTableData[] = response.data.tenants.map((tenant, index) => ({
        ...tenant,
        key: tenant.id.toString(),
        no: (pagination.current - 1) * pagination.pageSize + index + 1,
      }));

      setTenants(tableData);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.totalItems,
      }));
    } catch (error) {
      message.error('입주사 정보를 불러오는데 실패했습니다.');
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [pagination.current, pagination.pageSize, filters.status, filters.approvalStatus, filters.search]);

  const columns: ColumnsType<TenantTableData> = [
    {
      title: 'No.',
      key: 'no',
      width: 60,
      align: 'center',
      render: (_: any, __: any, index: number) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: '노출여부',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => {
        return status === 'active' ? '노출' : '비노출';
      },
    },
    {
      title: '입주사명',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '내용',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '이벤트 여부',
      dataIndex: 'isEventContent',
      key: 'isEventContent',
      width: 100,
      align: 'center',
      render: (isEvent: boolean) => isEvent ? 'Y' : 'N',
    },
    {
      title: '관리자명',
      key: 'adminName',
      width: 120,
      align: 'center',
      render: (record: any) => record.creator?.name || record.updater?.name || 'Admin1234',
    },
    {
      title: '등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center',
      render: (date: string) => date ? new Date(date).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\./g, '.').replace(/ /g, '') : '0000.00.00',
    },
    {
      title: '상태',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      width: 100,
      align: 'center',
      render: (status: string) => {
        const statusMap = {
          pending: { text: '승인대기', color: 'default' },
          approved: { text: '승인완료', color: 'blue' },
          rejected: { text: '승인거부', color: 'red' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap] || { text: '승인대기', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
  ];

  // 검색 및 필터 핸들러
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };


  // 일괄 삭제 기능
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    showConfirmModal({
      title: '알림',
      content: `삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?`,
      okText: '확인',
      okType: 'danger',
      onConfirm: async () => {
        try {
          const ids = selectedRowKeys.map(key => Number(key));
          await tenantService.bulkDeleteTenants(ids);
          message.success("삭제가 완료되었습니다.");
          setSelectedRowKeys([]);
          fetchTenants();
        } catch (error: any) {
          console.error('Bulk delete error:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  // 일괄 상태 변경 기능
  const handleBulkStatusChange = async (status: 'active' | 'inactive') => {
    if (selectedRowKeys.length === 0) {
      message.warning('상태를 변경할 항목을 선택해주세요.');
      return;
    }

    const statusText = status === 'active' ? '노출' : '비노출';

    try {
      const updatePromises = selectedRowKeys.map(key => {
        const tenant = tenants.find(t => t.key === key);
        if (tenant) {
          // status만 명시적으로 전달 (노출/비노출 로그를 위해)
          return tenantService.updateTenant(tenant.id, {
            name: tenant.name,
            status
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
      message.success(statusText === "노출" ? "노출 처리 되었습니다." : "비노출 처리 되었습니다.");
      setSelectedRowKeys([]);
      fetchTenants();
    } catch (error: any) {
      console.error('Bulk status change error:', error);
      message.error('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '건물 안내' },
          { title: '입주사 소개' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        입주사 소개
      </Title>

      {/* 필터 및 검색 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>노출여부</Text>
            <Select
              defaultValue="전체"
              style={{ width: 120 }}
              onChange={(value) => {
                const status = value === '전체' ? undefined : (value === '노출' ? 'active' : 'inactive');
                setFilters(prev => ({ ...prev, status }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              options={[
                { value: '전체', label: '전체' },
                { value: '노출', label: '노출' },
                { value: '비노출', label: '비노출' },
              ]}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>상태</Text>
            <Select
              defaultValue="전체"
              style={{ width: 120 }}
              onChange={(value) => {
                const approvalStatus = value === '전체' ? undefined : value as 'pending' | 'approved' | 'rejected';
                setFilters(prev => ({ ...prev, approvalStatus }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              options={[
                { value: '전체', label: '전체' },
                { value: 'pending', label: '승인대기' },
                { value: 'approved', label: '승인완료' },
                { value: 'rejected', label: '승인거부' },
              ]}
            />
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/tenant-intro/add')}>
          추가
        </Button>
      </div>

      {/* 구분선 */}
      <div style={{
        height: '1px',
        backgroundColor: '#303030',
        margin: '24px 0'
      }} />

      {/* 테이블 상단 정보 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Text>총 {pagination.total}건</Text>
          <Button
            onClick={handleBulkDelete}
            disabled={selectedRowKeys.length === 0}
            danger
            size="middle"
          >
            삭제
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            onClick={() => handleBulkStatusChange('inactive')}
            disabled={selectedRowKeys.length === 0}
            size="middle"
          >
            비노출
          </Button>
          <Button
            onClick={() => handleBulkStatusChange('active')}
            disabled={selectedRowKeys.length === 0}
            size="middle"
          >
            노출
          </Button>
          <Input
            placeholder="검색"
            suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
            style={{
              width: 400,
              height: 40,
              borderRadius: 8,
              background: '#2a2a2a',
              border: '1px solid #404040',
              color: '#ffffff'
            }}
            onChange={(e) => handleSearch(e.target.value)}
            value={filters.search}
            allowClear
          />
        </div>
      </div>

      {/* 테이블 */}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={tenants}
        loading={loading}
        onRow={(record) => ({
          onClick: () => {
            navigate(`/tenant-intro/detail/${record.id}`);
          },
          style: { cursor: 'pointer' }
        })}
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: false,
          pageSizeOptions: ['10', '20', '50'],
          style: { textAlign: 'right' },
          onChange: (page, pageSize) => {
            setPagination(prev => ({
              ...prev,
              current: page,
              pageSize: pageSize || prev.pageSize,
            }));
          },
        }}
        style={{
          backgroundColor: '#1f1f1f',
        }}
        size="middle"
      />
    </Space>
  );
};

export default TenantIntro;